import classNames from 'classnames'
import React, { useRef, useState } from 'react'
import ColorButton from '../common/ColorButton'
import styles from './style.module.scss'

import { OmButton } from '@owma/kit'
import { basicColors } from 'utils/ui'
import { isOrgSharedAccountEmail } from 'utils/orgSharedAccount'

import { useTranslation } from 'react-i18next'

interface Folder {
  displayName?: string
  color?: string
  url?: string
  isShared?: boolean
  isReadOnly?: boolean
  owner?: string
  calendarColor?: {
    _cdata: string
  }
}

interface FolderData {
  displayName: string
  color: string
}

interface FolderEditorProps {
  folder?: Folder | null
  onSave: (data: FolderData) => void
  onClose: () => void
  isCalendarFolder?: boolean
}

const FolderEditor: React.FC<FolderEditorProps> = ({
  folder,
  onSave,
  onClose,
  isCalendarFolder = true,
}) => {
  const [name, setName] = useState(folder?.displayName || '')
  const [color, setColor] = useState(
    folder?.color || basicColors[Math.floor(Math.random() * basicColors.length)],
  )

  const { t } = useTranslation()

  // 조직 공용 캘린더는 이름을 변경할 수 없도록 비활성화
  const isOrgSharedFolder = !!folder?.isShared && isOrgSharedAccountEmail(folder?.owner)

  // 초기값 저장
  const initialName = useRef(folder?.displayName || '')
  const initialColor = useRef(
    folder?.color || basicColors[Math.floor(Math.random() * basicColors.length)],
  )

  const isModified = (): boolean => {
    if (!name.trim()) return false

    if (folder) {
      // 수정 모드: 초기값과 다른지 확인
      return name !== initialName.current || color !== initialColor.current
    } else {
      // 생성 모드: 값이 있는지 확인
      return name.trim().length > 0
    }
  }

  const handleSubmit = (): void => {
    onSave({
      displayName: name,
      color: color,
    })
  }

  return (
    <div className={styles.editor}>
      <div className={styles.header}>
        <h2>
          {isCalendarFolder
            ? folder
              ? t('calendar.edit.edit')
              : t('calendar.edit.create')
            : folder
              ? t('todo.editor.edit')
              : t('todo.editor.create')}
        </h2>
      </div>
      <div className={styles.content}>
        <div className={styles.formGroup}>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={
              isCalendarFolder
                ? t('calendar.edit.calendarNamePlaceholder')
                : t('todo.editor.todoNamePlaceholder')
            }
            disabled={isOrgSharedFolder}
            autoFocus={!isOrgSharedFolder}
          />
        </div>
        <div className={styles.formGroup}>
          <ColorButton color={color} onChange={setColor} />
        </div>
      </div>

      <div style={{ marginTop: '24px' }}></div>
      <div className={styles.footer}>
        <div className={styles.footerButtons}>
          {folder && (
            <>
              <button className={styles.cancelButton} onClick={onClose}>
                {t('common.button.cancel')}
              </button>
              <OmButton
                className={classNames('type1', 'w100', 'h38')}
                disabled={!isModified()}
                onClick={() => {
                  handleSubmit()
                }}
              >
                {t('common.button.save')}
              </OmButton>
            </>
          )}
          {!folder && (
            <>
              <button className={styles.cancelButton} onClick={onClose}>
                {t('common.button.cancel')}
              </button>
              <button
                className={styles.saveButton}
                data-disabled={!isModified()}
                onClick={() => {
                  if (isModified()) {
                    handleSubmit()
                  }
                }}
              >
                {t('common.button.create')}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default FolderEditor
